/**
 * Time tracking (spec §9.6, §8.1): entries live in monthly shards `<ws>:timelog:YYYY-MM`; the running
 * timer is an open entry (`end = null`) referenced by `core.tracking.current` (I-6: elapsed is computed).
 */
import { LoroMap, type LoroDoc } from "loro-crdt";
import { ulid } from "ulid";
import { commitMessage } from "../core/actor";
import { C, timelogDocId } from "../core/schema";
import type { TimelogEntry } from "../core/computed";
import { docManager, type DocHandle } from "../sync/docManager";

export interface TrackingCurrent {
  taskId: string;
  entryId: string;
  /** start of the running (or, when paused, the last) stretch */
  startedAt: string;
  docId: string;
  /** set while paused: the entry is closed then, and resuming opens a new one */
  pausedAt?: string | null;
  /** time of the earlier stretches of this session (before a pause), so the clock shows the whole session */
  priorMs?: number;
  /** "pomodoro" when a Pomodoro break paused it (auto-pause): only such a pause is resumed automatically */
  pausedBy?: string | null;
}

export function yearMonth(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function shardIdFor(workspaceId: string, at: Date): string {
  return timelogDocId(workspaceId, yearMonth(at));
}

/**
 * In a shared space tracking state is per profile: `tracking.byProfile[<profileId>].current`
 * (spec §9.29). The solo shape (`tracking.current`) is the single-key case. The scope is set once
 * by the shell from the session and the workspace.
 */
let scope: string | null = null;
export function setTrackingScope(profileId: string | null): void {
  scope = profileId;
}

export function readCurrent(core: LoroDoc): TrackingCurrent | null {
  const t = core.getMap(C.tracking).toJSON() as {
    current?: TrackingCurrent | null;
    byProfile?: Record<string, { current?: TrackingCurrent | null }>;
  };
  const v = scope ? t.byProfile?.[scope]?.current : t.current;
  return v && typeof v === "object" && v.entryId ? v : null;
}

/** Everyone currently tracking in this workspace (kiosk card per person). */
export function readAllCurrent(core: LoroDoc): { profileId: string | null; current: TrackingCurrent }[] {
  const t = core.getMap(C.tracking).toJSON() as {
    current?: TrackingCurrent | null;
    byProfile?: Record<string, { current?: TrackingCurrent | null }>;
  };
  const out: { profileId: string | null; current: TrackingCurrent }[] = [];
  if (t.current?.entryId) out.push({ profileId: null, current: t.current });
  for (const [pid, v] of Object.entries(t.byProfile ?? {}))
    if (v?.current?.entryId) out.push({ profileId: pid, current: v.current });
  return out;
}

export function entriesOf(shard: LoroDoc): TimelogEntry[] {
  const all = shard.getMap(C.entries).toJSON() as Record<string, TimelogEntry>;
  return Object.values(all).filter((e) => e && typeof e === "object" && typeof e.start === "string");
}

function entryMap(shard: LoroDoc, entryId: string): LoroMap | undefined {
  const m = shard.getMap(C.entries).get(entryId);
  return m && typeof m === "object" && "set" in m ? (m as LoroMap) : undefined;
}

export function addEntry(
  shard: LoroDoc,
  e: { taskId: string; start: string; end: string | null; note?: string; id?: string },
): string {
  const id = e.id ?? ulid();
  const m = shard.getMap(C.entries).setContainer(id, new LoroMap());
  m.set("id", id);
  m.set("taskId", e.taskId);
  m.set("start", e.start);
  m.set("end", e.end);
  if (e.note) m.set("note", e.note);
  shard.commit({ message: commitMessage({ op: "addEntry" }) });
  return id;
}

export function updateEntry(
  shard: LoroDoc,
  entryId: string,
  patch: { start?: string; end?: string | null; note?: string; taskId?: string },
): void {
  const m = entryMap(shard, entryId);
  if (!m) throw new Error("timelog.noSuchEntry");
  for (const [k, v] of Object.entries(patch)) if (v !== undefined) m.set(k, v as never);
  shard.commit({ message: commitMessage({ op: "updateEntry" }) });
}

export function deleteEntry(shard: LoroDoc, entryId: string): void {
  shard.getMap(C.entries).delete(entryId);
  shard.commit({ message: commitMessage({ op: "deleteEntry" }) });
}

function scopedTracking(core: LoroDoc): LoroMap {
  const root = core.getMap(C.tracking);
  if (!scope) return root;
  const by = root.get("byProfile") as unknown;
  const byMap = by instanceof LoroMap ? by : root.setContainer("byProfile", new LoroMap());
  const mine = byMap.get(scope) as unknown;
  return mine instanceof LoroMap ? mine : byMap.setContainer(scope, new LoroMap());
}

export interface FocusState {
  taskId: string;
  startedAt: string;
}

/** Focus mode's task, per person in a shared space like the running timer (one person's focus never replaces
 * another's). */
export function readFocus(core: LoroDoc): FocusState | null {
  const t = core.getMap(C.tracking).toJSON() as {
    focus?: FocusState | null;
    byProfile?: Record<string, { focus?: FocusState | null }>;
  };
  const v = scope ? t.byProfile?.[scope]?.focus : t.focus;
  return v && typeof v === "object" && v.taskId ? v : null;
}

export function setFocus(core: LoroDoc, taskId: string | null): void {
  const tracking = scopedTracking(core);
  if (taskId) {
    const m = tracking.setContainer("focus", new LoroMap());
    m.set("taskId", taskId);
    m.set("startedAt", new Date().toISOString());
  } else tracking.set("focus", null);
  core.commit({ message: commitMessage({ op: taskId ? "focusStart" : "focusEnd" }) });
}

function setCurrent(core: LoroDoc, cur: TrackingCurrent | null, op: string): void {
  const tracking = scopedTracking(core);
  if (cur) {
    const m = tracking.setContainer("current", new LoroMap());
    m.set("taskId", cur.taskId);
    m.set("entryId", cur.entryId);
    m.set("startedAt", cur.startedAt);
    m.set("docId", cur.docId);
    m.set("pausedAt", cur.pausedAt ?? null);
    m.set("priorMs", cur.priorMs ?? 0);
    m.set("pausedBy", cur.pausedAt ? (cur.pausedBy ?? null) : null);
  } else tracking.set("current", null);
  core.commit({ message: commitMessage({ op }) });
}

/** Stop the running entry (if any) at `now`. Opens the shard that holds it. */
export async function stopTracking(core: LoroDoc, now = new Date()): Promise<void> {
  const cur = readCurrent(core);
  if (!cur) return;
  const shard = await docManager.open(cur.docId);
  const m = entryMap(shard.doc, cur.entryId);
  if (m && m.get("end") === null) {
    m.set("end", now.toISOString());
    shard.doc.commit({ message: commitMessage({ op: "stopTracking" }) });
  }
  setCurrent(core, null, "stopTracking");
}

/** Start tracking a task: stops the previous one first (spec §9.6 "switching tasks stops the previous"). */
export async function startTracking(
  core: LoroDoc,
  workspaceId: string,
  taskId: string,
  now = new Date(),
): Promise<string> {
  const cur = readCurrent(core);
  if (cur?.taskId === taskId) {
    if (cur.pausedAt) return resumeTracking(core, workspaceId, now);
    return cur.entryId;
  }
  if (cur) await stopTracking(core, now);
  const docId = shardIdFor(workspaceId, now);
  const shard = await docManager.open(docId);
  const startedAt = now.toISOString();
  const entryId = addEntry(shard.doc, { taskId, start: startedAt, end: null });
  setCurrent(core, { taskId, entryId, startedAt, docId }, "startTracking");
  return entryId;
}

/**
 * Reconciliation rule (spec §9.1 "Conflict semantics"): when `tracking.current` moves away from an
 * entry that is still open, close it at `current.startedAt` (or at its own start if it began later).
 * Every peer applies the same rule, so the written value is identical everywhere (LWW converges).
 */
export function reconcileOpenEntries(core: LoroDoc, shard: DocHandle): number {
  const cur = readCurrent(core);
  const running = new Set(readAllCurrent(core).map((c) => c.current.entryId));
  let closed = 0;
  for (const e of entriesOf(shard.doc)) {
    if (e.end !== null) continue;
    if (running.has(e.id)) continue; // someone's current entry (mine or another person's)
    if (scope && cur === null) continue; // in a shared space only reconcile against my own timer
    const m = entryMap(shard.doc, e.id);
    if (!m) continue;
    if (cur) {
      // an entry that lost the race but started later than the winner closes at its own start
      // (zero length) so no open entry lingers; both branches are the same on every peer
      m.set("end", e.start < cur.startedAt ? cur.startedAt : e.start);
      closed++;
    }
    // no current and an open entry: a stop that raced; the daily worklog shows it as open for manual repair
  }
  if (closed) shard.doc.commit({ message: commitMessage({ op: "reconcileEntries" }) });
  return closed;
}

/** The session's time: earlier stretches plus the running one (frozen while paused). */
export function elapsedMs(cur: TrackingCurrent | null, nowMs: number): number {
  if (!cur) return 0;
  const until = cur.pausedAt ? Date.parse(cur.pausedAt) : nowMs;
  return (cur.priorMs ?? 0) + Math.max(0, until - Date.parse(cur.startedAt));
}

/** Pause: the running entry ends now (the worklog shows the real time); the timer stays on this task. */
export async function pauseTracking(
  core: LoroDoc,
  now = new Date(),
  by: string | null = null,
): Promise<void> {
  const cur = readCurrent(core);
  if (!cur || cur.pausedAt) return;
  const shard = await docManager.open(cur.docId);
  const m = entryMap(shard.doc, cur.entryId);
  if (m && m.get("end") === null) {
    m.set("end", now.toISOString());
    shard.doc.commit({ message: commitMessage({ op: "pauseTracking" }) });
  }
  setCurrent(core, { ...cur, pausedAt: now.toISOString(), pausedBy: by }, "pauseTracking");
}

/** Resume: a new entry for the same task; the clock goes on from the paused total. */
export async function resumeTracking(core: LoroDoc, workspaceId: string, now = new Date()): Promise<string> {
  const cur = readCurrent(core);
  if (!cur) throw new Error("tracking.notRunning");
  if (!cur.pausedAt) return cur.entryId;
  const docId = shardIdFor(workspaceId, now);
  const shard = await docManager.open(docId);
  const startedAt = now.toISOString();
  const entryId = addEntry(shard.doc, { taskId: cur.taskId, start: startedAt, end: null });
  const priorMs = (cur.priorMs ?? 0) + Math.max(0, Date.parse(cur.pausedAt) - Date.parse(cur.startedAt));
  setCurrent(
    core,
    { taskId: cur.taskId, entryId, startedAt, docId, pausedAt: null, priorMs },
    "resumeTracking",
  );
  return entryId;
}

/** Sum of today's entries in this shard (+ the running one), in minutes. */
export function minutesOnDay(entries: TimelogEntry[], day: Date, nowMs: number): number {
  const start = new Date(day);
  start.setHours(0, 0, 0, 0);
  const next = new Date(start);
  next.setDate(next.getDate() + 1); // 23 or 25 hours on the days the clocks change (bug hunt M12)
  const end = next.getTime();
  let ms = 0;
  for (const e of entries) {
    const s = Date.parse(e.start);
    const f = e.end ? Date.parse(e.end) : nowMs;
    const a = Math.max(s, start.getTime());
    const b = Math.min(f, end);
    if (b > a) ms += b - a;
  }
  return ms / 60_000;
}
